/**
 * Pure geometry utilities for label placement — Cohen-Sutherland line-rect
 * intersection, circle-rect tests, and label position computation. Extracted
 * from TreeDetailRenderer for independent testability.
 */

/** A line segment defined by its two endpoints. */
export interface LineSeg { x1: number; y1: number; x2: number; y2: number }

/** A candidate or resolved label position with text origin offsets. */
export interface LabelPlacement { x: number; y: number; originX: number; originY: number }

/** A circle obstacle in world space. */
export interface NodeCircle { x: number; y: number; r: number }

/**
 * Cohen-Sutherland line-rect intersection test.
 * Returns true if the line segment (x1,y1)→(x2,y2) intersects the axis-aligned
 * rectangle defined by [left, right] × [top, bottom].
 */
export function lineIntersectsRect(
  line: LineSeg, left: number, top: number, right: number, bottom: number,
): boolean {
  const outcode = (px: number, py: number): number => {
    let code = 0;
    if (px < left) code |= 1;
    else if (px > right) code |= 2;
    if (py < top) code |= 4;
    else if (py > bottom) code |= 8;
    return code;
  };

  let x0 = line.x1, y0 = line.y1, x1 = line.x2, y1 = line.y2;
  let code0 = outcode(x0, y0);
  let code1 = outcode(x1, y1);

  for (let iter = 0; iter < 10; iter++) {
    if ((code0 | code1) === 0) return true;
    if ((code0 & code1) !== 0) return false;

    const codeOut = code0 !== 0 ? code0 : code1;
    let x = 0, y = 0;
    if (codeOut & 8) { x = x0 + (x1 - x0) * (bottom - y0) / (y1 - y0); y = bottom; }
    else if (codeOut & 4) { x = x0 + (x1 - x0) * (top - y0) / (y1 - y0); y = top; }
    else if (codeOut & 2) { y = y0 + (y1 - y0) * (right - x0) / (x1 - x0); x = right; }
    else if (codeOut & 1) { y = y0 + (y1 - y0) * (left - x0) / (x1 - x0); x = left; }

    if (codeOut === code0) { x0 = x; y0 = y; code0 = outcode(x0, y0); }
    else { x1 = x; y1 = y; code1 = outcode(x1, y1); }
  }
  return false;
}

/**
 * Circle-rect intersection test.
 * Returns true if the circle at (cx, cy) with radius r overlaps the
 * axis-aligned rectangle defined by [left, right] × [top, bottom].
 */
export function circleIntersectsRect(
  cx: number, cy: number, r: number,
  left: number, top: number, right: number, bottom: number,
): boolean {
  const nearestX = Math.max(left, Math.min(cx, right));
  const nearestY = Math.max(top, Math.min(cy, bottom));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  return dx * dx + dy * dy < r * r;
}

/**
 * Candidate label positions relative to a node center, expressed as
 * [labelX, labelY, originX, originY].
 *
 * @param nodeX       World-space center X of the node.
 * @param nodeY       World-space center Y of the node.
 * @param nodeRadius  Visual radius of the node (clearance gap applied internally).
 * @param gap         Pixel gap between node edge and label edge.
 */
export function buildLabelCandidates(
  nodeX: number, nodeY: number, nodeRadius: number, gap: number,
): [number, number, number, number][] {
  return [
    [nodeX,              nodeY + nodeRadius + gap, 0.5, 0],   // below
    [nodeX,              nodeY - nodeRadius - gap, 0.5, 1],   // above
    [nodeX + nodeRadius + gap, nodeY,              0,   0.5], // right
    [nodeX - nodeRadius - gap, nodeY,              1,   0.5], // left
  ];
}

/**
 * Score a single label candidate rectangle against a set of branch lines and
 * node circles. Lower scores are preferred (fewer collisions).
 *
 * @param cx        Candidate label X.
 * @param cy        Candidate label Y.
 * @param ox        Origin X (0–1).
 * @param oy        Origin Y (0–1).
 * @param textW     Measured text width in world pixels.
 * @param textH     Estimated text height in world pixels.
 * @param lines     Branch line segments to test against.
 * @param circles   Node circles to test against (the owning node should be excluded by caller).
 */
export function scoreLabelCandidate(
  cx: number, cy: number, ox: number, oy: number,
  textW: number, textH: number,
  lines: LineSeg[], circles: NodeCircle[],
): number {
  const left   = cx - textW * ox;
  const top    = cy - textH * oy;
  const right  = left + textW;
  const bottom = top  + textH;

  let score = 0;
  for (const line of lines) {
    if (lineIntersectsRect(line, left, top, right, bottom)) score += 1;
  }
  for (const nc of circles) {
    if (circleIntersectsRect(nc.x, nc.y, nc.r, left, top, right, bottom)) score += 2;
  }
  return score;
}

/**
 * Pick the best placement from the four candidates by minimizing collision score.
 * Returns the winning [x, y, originX, originY] tuple.
 */
export function pickBestCandidate(
  candidates: [number, number, number, number][],
  textW: number, textH: number,
  lines: LineSeg[], circles: NodeCircle[],
): [number, number, number, number] {
  let bestIdx = 0;
  let bestScore = Infinity;

  for (let i = 0; i < candidates.length; i++) {
    const [cx, cy, ox, oy] = candidates[i];
    const score = scoreLabelCandidate(cx, cy, ox, oy, textW, textH, lines, circles);
    if (score < bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  return candidates[bestIdx];
}
